// Rep identity, role, and working-state view over the HubSpot session.
//
// WorkspaceChrome and role gates read from here instead of the JAMIE
// fixture. The hook is tolerant of the three states the SessionProvider
// may be in: `pending` (handshake in flight), `ready` / `refreshing`
// (session usable), and `expired` / `error` (no usable session). The
// `rail` component renders a loading pill while pending.
//
// Role resolution is deliberately conservative. The authenticated session
// carries `userId` + `email`; the server-side identity payload is where
// the rep's HubSpot team / role lives. This hook reads, in order, the
// `?role=manager` / `?role=executive` query flag used by the design
// review, then the `role` the session carries (W37b, 2026-10-20: the
// handshake mirrors the api-server's WORKSPACE_MANAGER_USER_IDS rule, see
// auth/workspace-role.ts), then the dev mock default. Non-manager reps
// default to `sales_rep` which blocks `/manager`, `/team-live`, and
// `/executive-live` through RequireRole.

import { useSearchParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useOptionalSession } from '../../auth/session-context.js';
import type { HubSpotSession } from '../../auth/hubspot-handshake.js';
import { isDevMockSession } from '../../auth/mockContext.js';

export type RepRole = 'sales_rep' | 'manager' | 'executive';

export type RepAvailability =
  | 'available'
  | 'on-call'
  | 'off-shift'
  | 'unknown';

export interface Rep {
  id: string;
  initials: string;
  fullName: string;
  email: string;
  role: RepRole;
  availability: RepAvailability;
}

export type RepState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'ready'; rep: Rep };

const RepOverrideContext = createContext<Rep | null>(null);

export interface RepOverrideProviderProps {
  rep: Rep;
  children: ReactNode;
}

/**
 * Test / design-review hook: supply a specific rep without wiring the
 * full HubSpot handshake. Production never mounts this provider.
 */
export function RepOverrideProvider({
  rep,
  children,
}: RepOverrideProviderProps): ReactNode {
  return (
    <RepOverrideContext.Provider value={rep}>
      {children}
    </RepOverrideContext.Provider>
  );
}

function deriveInitials(fullName: string, email: string): string {
  const parts = fullName.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
  }
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return 'US';
}

function deriveNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  if (!local) return email;
  const words = local.split(/[._-]+/).filter(Boolean);
  if (!words.length) return email;
  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function pickRole(
  roleFlag: string | null,
  devMock: boolean,
  sessionRole: RepRole | null = null,
): RepRole {
  if (roleFlag === 'manager') return 'manager';
  if (roleFlag === 'executive') return 'executive';
  if (roleFlag === 'sales_rep' || roleFlag === 'sales-rep') return 'sales_rep';
  // W37b: the role the session carries (url-bootstrap deep link user id
  // or signed payload) is what the api-server also resolved.
  if (sessionRole) return sessionRole;
  // Dev mock defaults to the manager role so the dev workflow can review
  // every rail. Production has no `?role=` flag and no dev mock, so the
  // fallback is sales_rep.
  if (devMock) return 'manager';
  return 'sales_rep';
}

function pickAvailability(
  availFlag: string | null,
  devMock: boolean,
): RepAvailability {
  if (availFlag === 'on-call') return 'on-call';
  if (availFlag === 'off-shift') return 'off-shift';
  if (availFlag === 'unknown') return 'unknown';
  if (availFlag === 'available') return 'available';
  return devMock ? 'available' : 'unknown';
}

function sessionToRep(
  session: HubSpotSession,
  roleFlag: string | null,
  availFlag: string | null,
): Rep {
  const fullName = deriveNameFromEmail(session.email);
  const devMock = isDevMockSession(session);
  const sessionRole: RepRole | null =
    session.role === 'manager' || session.role === 'executive' || session.role === 'sales_rep'
      ? session.role
      : null;
  return {
    id: session.userId,
    initials: deriveInitials(fullName, session.email),
    fullName,
    email: session.email,
    role: pickRole(roleFlag, devMock, sessionRole),
    availability: pickAvailability(availFlag, devMock),
  };
}

/**
 * Return the current rep view. Suitable for the chrome header, role
 * guards, and the Start / End Working controls.
 */
export function useRep(): RepState {
  const override = useContext(RepOverrideContext);
  const [search] = useSearchParams();
  const session = useOptionalSession();
  const roleFlag = search.get('role');
  const availFlag = search.get('availability');

  return useMemo<RepState>(() => {
    if (override) return { status: 'ready', rep: override };
    if (!session) return { status: 'unauthenticated' };
    if (session.status === 'pending') return { status: 'loading' };
    if (session.status === 'error' || session.status === 'expired') {
      return { status: 'unauthenticated' };
    }
    if (!session.session) return { status: 'loading' };
    return {
      status: 'ready',
      rep: sessionToRep(session.session, roleFlag, availFlag),
    };
  }, [override, session, roleFlag, availFlag]);
}

// Narrower helper: throws when no rep is loaded, for callers already
// inside a <RepGate /> or RequireRole wrapper that handles the fallback.
export function useRequireRep(): Rep {
  const state = useRep();
  if (state.status !== 'ready') {
    throw new Error(
      `useRequireRep() called with status=${state.status}. Wrap the caller in a rep gate.`,
    );
  }
  return state.rep;
}

export const __test__ = {
  sessionToRep,
  deriveNameFromEmail,
  pickRole,
  pickAvailability,
};
